import { getPageLinkByNodeId, getPageLinkByPageType, LinkModel, useGetLinks } from "@aspen/Store/Aspen/AspenLinksSelectors";
import AspenAccountDetailsDisplay from "@aspen/Store/Aspen/Components/AspenAccountDetailsDisplay";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest, getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as React from "react";
import { FC } from "react";
import { ResolveThunks } from "react-redux";

const enum fields {
    className = "className",
    showFirstName = "showFirstName",
    firstNamePrefix = "firstNamePrefix",
    showCompanyName = "showCompanyName",
    companyNamePrefix = "companyNamePrefix",
    showCity = "showCity",
    cityPrefix = "cityPrefix",
    showCustomerNumber = "showCustomerNumber",
    customerNumberPrefix = "customerNumberPrefix",
    includeAccountLink = "includeAccountLink",
    includeSignoutLink = "includeSignoutLink",
    showHorizontalLine = "showHorizontalLine",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.className]: string;
        [fields.showFirstName]: boolean;
        [fields.firstNamePrefix]: string;
        [fields.showCompanyName]: boolean;
        [fields.companyNamePrefix]: string;
        [fields.showCity]: boolean;
        [fields.cityPrefix]: string;
        [fields.showCustomerNumber]: boolean;
        [fields.customerNumberPrefix]: string;
        [fields.includeAccountLink]: boolean;
        [fields.includeSignoutLink]: boolean;
        [fields.showHorizontalLine]: boolean;
        [fields.links]: LinkModel[];
    };
}
const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currentShipToState: getCurrentShipToState(state),
    session: getSession(state),
    userName: state.context.session.userName,
    firstName: state.context.session.firstName,
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    isPunchOutSession: getIsPunchOutSession(state),
    currentUserIsGuest: getCurrentUserIsGuest(state),
});
const mapDispatchToProps = {
    loadCurrentShipTo,
    signOut,
    cancelPunchOut
};
type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const AspenMyAccountLinkList: FC<Props> = ({ fields, myAccountPageUrl,userName, firstName, currentShipToState, isPunchOutSession, cancelPunchOut, signOut, currentUserIsGuest }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    return (
        <>
            <AspenAccountDetailsDisplay fields={fields} links={links as PageLinkModel[]}></AspenAccountDetailsDisplay>
        </>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: AspenMyAccountLinkList,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.className,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.showFirstName,
                fieldType: "Translatable",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
            {
                name: fields.firstNamePrefix,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: (item: HasFields) => item.fields.showFirstName === true,
                tab: basicTab,
            },
            {
                name: fields.showCompanyName,
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
            },
            {
                name: fields.companyNamePrefix,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: (item: HasFields) => item.fields.showCompanyName === true,
                tab: basicTab,
            }, {
                name: fields.showCity,
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
            }, {
                name: fields.cityPrefix,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: (item: HasFields) => item.fields.showCity === true,
                tab: basicTab,
            }, {
                name: fields.showCustomerNumber,
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
            }, {
                name: fields.customerNumberPrefix,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: (item: HasFields) => item.fields.showCustomerNumber === true,
                tab: basicTab,
            }, {
                name: fields.includeAccountLink,
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
            },
            {
                name: fields.includeSignoutLink,
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
            },
            {
                name: fields.showHorizontalLine,
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: true,
            },

            {
                name: fields.links,
                displayName: "Additional Links",
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "titlePrefix",
                        displayName: "Title Prefix (Without Link)",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
                tab: basicTab,
            }
        ],
    },
};

export default widgetModule;
